// components/WorkbookTrackCard.jsx
// The workbook track (bundle) card, extracted verbatim from WorkbookCatalog's
// inline renderCard so the Workbooks catalog and the My Courses page render the
// same card. Purely presentational: the bundle object and an onOpen handler
// arrive as props instead of being closed over. No em or en dashes.
//
// The root sets its own font-family and text color (Plus Jakarta Sans / navy)
// so the card looks identical on a page whose body font differs (My Courses
// runs DM Sans). On the Workbooks catalog these already match the inherited
// values, so nothing changes there.

const C = { card: "#FFFFFF", navy: "#1a1a2e", orange: "#EA580C", line: "#E4DFD4", muted: "#6B6458" };
const inr = (n) => "₹" + n.toLocaleString("en-IN");

// Inline arrow icon. The mockup used lucide-react, which is not a dependency of
// this project, so we ship a tiny dependency-free SVG with the same size prop.
const ArrowRight = ({ size = 15 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h14" />
    <path d="M13 6l6 6-6 6" />
  </svg>
);

// Dependency-free people icon for the enrolled count.
const UsersIcon = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

// Card-specific CSS. Pages render this once inside their own <style> block,
// matching this codebase's page-owns-its-styles convention (see OwnedCourseCard).
// .frns is included because the card title uses it; a host page may not define
// it otherwise.
export const WORKBOOK_TRACK_CARD_CSS = `
  .frns{font-family:'Fraunces',serif;}
  .bcard{transition:transform .15s ease,border-color .15s ease;}
  .bcard:hover{transform:translateY(-3px);border-color:var(--ac);}
  .cta:hover{filter:brightness(.94);}
`;

export default function WorkbookTrackCard({ bundle: b, onOpen }) {
  const free = b.free || b.owned;
  const disc = b.originalPrice && b.price ? Math.round((1 - b.price / b.originalPrice) * 100) : 0;
  // Enrolled shows for any paid bundle that has a count worth showing. Hidden
  // for the free bundle, whose count is total signups rather than buyers, and
  // for a zero, which reads worse than no number at all. The per-track special
  // cases that used to live here (and a fabricated default of 5 for AutoCAD)
  // are gone: the baselines sit with the real counts in the API.
  const showEnrolled = b.track !== "free" && b.enrolled != null && b.enrolled > 0;
  // Whether this bundle also grants the C# foundation workbooks, read from the
  // catalogue rather than guessed from the track name: it is a column on the
  // bundle and the same flag the grant path applies, so the card cannot promise
  // something the purchase does not deliver. Hidden on coming-soon cards, where
  // "when you buy" would be misleading.
  const includesFoundation = !!b.includesFoundation && !b.comingSoon;
  return (
    <div key={b.track} className="bcard" onClick={onOpen} style={{ "--ac": b.accent, fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", color: C.navy, background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, padding: 26, cursor: "pointer", display: "flex", flexDirection: "column", position: "relative", opacity: b.comingSoon ? 0.78 : 1 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <span style={{ width: 10, height: 10, borderRadius: 3, background: b.accent }} />
        <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: b.accent }}>{b.tag}</span>
        {b.comingSoon && <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 700, color: C.muted, border: `1px solid ${C.line}`, padding: "2px 8px", borderRadius: 20 }}>Soon</span>}
      </div>
      <h2 className="frns" style={{ fontSize: 25, fontWeight: 600, margin: "0 0 10px" }}>{b.title}</h2>
      <p style={{ fontSize: 14.5, color: C.muted, lineHeight: 1.5, margin: "0 0 14px", flex: 1 }}>{b.blurb}</p>
      {includesFoundation && (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, lineHeight: 1.4, color: C.navy, background: `${b.accent}14`, border: `1px solid ${b.accent}33`, borderRadius: 8, padding: "7px 10px", marginBottom: 14 }}>
          <span style={{ flexShrink: 0, color: b.accent, fontWeight: 700 }}>✓</span>
          <span>{b.owned ? "C# Foundation included, already unlocked." : "Buy this and get the full C# Mastery track free."}</span>
        </div>
      )}
      {(b.rating != null || showEnrolled) && (
        <div style={{ display: "flex", alignItems: "center", gap: 11, fontSize: 14.5, color: C.muted, marginBottom: 12 }}>
          {b.rating != null && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span style={{ color: C.orange, fontSize: 16 }}>★</span>
              <span style={{ fontWeight: 700, color: C.navy }}>{b.rating.toFixed(1)}</span>
              <span style={{ fontSize: 13, color: C.muted }}>({b.ratingCount})</span>
            </span>
          )}
          {b.rating != null && showEnrolled && <span style={{ color: "#b8ad9e" }}>&middot;</span>}
          {showEnrolled && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#3a3a48" }}>
              <UsersIcon size={15} /> <span><span style={{ fontWeight: 600 }}>{b.enrolled}</span> enrolled</span>
            </span>
          )}
        </div>
      )}
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 18 }}>{b.workbooks} workbooks</div>
      <div style={{ borderTop: `1px solid ${C.line}`, paddingTop: 18, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {b.comingSoon ? (
          <>
            <span style={{ fontSize: 14, fontWeight: 700, color: C.muted }}>Launching soon</span>
            <span className="cta" style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "transparent", color: C.muted, border: `1.5px solid ${C.line}`, borderRadius: 9, padding: "9px 16px", fontSize: 14, fontWeight: 700 }}>
              View details <ArrowRight size={15} />
            </span>
          </>
        ) : (
          <>
            <div>
              {free ? (
                <span style={{ fontSize: 14, fontWeight: 700, color: "#16855c" }}>{b.owned ? "You own this" : "Free to read"}</span>
              ) : b.price != null ? (
                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <span className="frns" style={{ fontSize: 24, fontWeight: 700 }}>{inr(b.price)}</span>
                  {b.originalPrice && <span style={{ fontSize: 14, color: "#b0a896", textDecoration: "line-through" }}>{inr(b.originalPrice)}</span>}
                  {disc > 0 && <span style={{ fontSize: 10.5, fontWeight: 700, color: C.orange, background: "#fdeee4", padding: "2px 7px", borderRadius: 20 }}>{disc}% off</span>}
                </div>
              ) : (
                <span style={{ fontSize: 14, fontWeight: 700, color: C.muted }}>View details</span>
              )}
            </div>
            <span className="cta" style={{ display: "inline-flex", alignItems: "center", gap: 7, background: free ? "transparent" : b.accent, color: free ? b.accent : "#fff", border: free ? `1.5px solid ${b.accent}` : "none", borderRadius: 9, padding: "9px 16px", fontSize: 14, fontWeight: 700 }}>
              {b.owned ? "Continue" : free ? "Start learning" : "View bundle"} <ArrowRight size={15} />
            </span>
          </>
        )}
      </div>
    </div>
  );
}
